import { useState } from 'react';
import { colour, space, text } from '../design/tokens.ts';
import { Button, Card, Field, Page, Select, Status, TextInput } from '../components/basics.tsx';
import { CouldNotLoad, DoneToast, Loading } from '../components/states.tsx';
import { Dialog } from '../components/Dialog.tsx';
import { api } from '../api/client.ts';
import { useLoad } from '../app/useLoad.ts';
import { useCan } from '../app/me.tsx';
import type { BoardFilter } from './board/types.ts';

/**
 * Screen 7: re-run everything leading up to the movie, for the passes that
 * match.
 *
 * The totals are shown before anybody confirms, per process, with the skipped
 * list and its reasons. The start points come from the project's own pipeline —
 * "Everything after Stabilize and Fix" exists only if that project has that
 * step — and blocked passes are never in a plan, which is the rules' doing
 * rather than this screen's.
 */

/** A start point is a structure, not a key: `{mode:"auto"}`, `{start_at}`, `{after}`. */
type StartPoint = { mode: 'auto' } | { start_at: string } | { after: string };

interface Plan {
  start: StartPoint;
  order: string[];
  totals_by_process: Record<string, number>;
  totals_by_step: Record<string, number>;
  runs: number;
  rows: { pass_id: string; vc_id: string; will_run_labels: string[] }[];
  skipped: { pass_id: string; vc_id: string; reason: string }[];
  needs_confirming: boolean;
  confirm_above: number;
}

export function FinalMovie({ projectId }: { projectId: string }) {
  const can = useCan();
  const [which, setWhich] = useState(0);
  const [owner, setOwner] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const filter: BoardFilter = { project_id: projectId };

  const { state: points } = useLoad<{ start_points: { label: string; start: StartPoint }[] }>(
    () => api.get(`/api/projects/${encodeURIComponent(projectId)}/final-movie/start-points`),
    [projectId]
  );
  const choices = points.data?.start_points ?? [];
  const start = choices[which]?.start ?? { mode: 'auto' as const };

  const { state, reload } = useLoad<Plan>(
    () => api.post('/api/final-movie/plan', { filter, start }),
    [projectId, which, choices.length]
  );
  const plan = state.data;

  const launch = async (): Promise<void> => {
    try {
      const result = await api.post<{ launched: unknown[]; failed: unknown[] }>(
        '/api/final-movie/launch',
        { filter, start, confirmed: true, ...(owner ? { owner } : {}) }
      );
      setConfirming(false);
      setToast(
        `Launched ${result.launched.length} runs. Each one reset its automatic count.${result.failed.length ? ` ${result.failed.length} could not start.` : ''}`
      );
      reload();
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'Those could not be launched.');
    }
  };

  return (
    <Page
      eyebrow="MAESTRO · FINAL MOVIE"
      title="Final movie"
      description="Re-run what is needed to get every pass to a movie. The totals are shown before anything starts."
      action={
        can.launch && plan && plan.runs > 0 ? (
          <Button tone="primary" onClick={() => setConfirming(true)}>
            Launch {plan.runs} {plan.runs === 1 ? 'run' : 'runs'}
          </Button>
        ) : undefined
      }
    >
      <Card>
        <Field label="Where to start">
          <Select
            value={String(which)}
            onChange={(v) => setWhich(Number(v))}
            options={choices.map((p, i) => ({ value: String(i), label: p.label }))}
          />
        </Field>
        <span style={{ ...text.small, color: colour.inkSoft }}>
          {'mode' in start
            ? 'Per pass, start at the first stale or missing step and run everything after it. Steps that are current are left alone, and a pass that is stale but kept as it is on purpose is skipped.'
            : 'Run these steps even where they are current, because you asked for that. A pass with no result for the step before is skipped.'}
        </span>
      </Card>

      {state.status === 'loading' && !plan && <Loading says="Working out what that would run…" rows={4} />}
      {state.status === 'failed' && !plan && (
        <CouldNotLoad what="the plan" error={state.error} onRetry={reload} />
      )}

      {plan && (
        <>
          <Card title="What that would run" aside={`${plan.runs} runs in total`}>
            {Object.keys(plan.totals_by_process).length === 0 ? (
              <span style={{ ...text.body, color: colour.inkSoft }}>
                Nothing to do. Everything that can be made is current.
              </span>
            ) : (
              Object.entries(plan.totals_by_process).map(([process, n]) => (
                <div key={process} style={{ display: 'flex', justifyContent: 'space-between', gap: space.control }}>
                  <span style={text.small}>{process}</span>
                  <span style={text.bodyStrong}>{n}</span>
                </div>
              ))
            )}
          </Card>

          {plan.skipped.length > 0 && (
            <Card title="Skipped" aside={`${plan.skipped.length} passes, and why`}>
              {plan.skipped.slice(0, 20).map((s) => (
                <div key={s.pass_id} style={{ display: 'flex', gap: space.tight, alignItems: 'center' }}>
                  <span style={text.monoStrong}>{s.vc_id}</span>
                  <span style={{ ...text.note, color: colour.inkSoft }}>{s.reason}</span>
                </div>
              ))}
              {plan.skipped.length > 20 && (
                <span style={{ ...text.note, color: colour.inkFaint }}>
                  and {plan.skipped.length - 20} more
                </span>
              )}
            </Card>
          )}

          {plan.rows.length > 0 && (
            <Card title="Per pass" aside="What each one would run, in order.">
              {plan.rows.slice(0, 40).map((row) => (
                <div key={row.pass_id} style={{ display: 'flex', flexWrap: 'wrap', gap: space.tight, alignItems: 'center' }}>
                  <span style={text.monoStrong}>{row.vc_id}</span>
                  <span style={{ ...text.note, color: colour.inkSoft }}>
                    Will run: {row.will_run_labels.join(' → ')}
                  </span>
                </div>
              ))}
              {plan.rows.length > 40 && (
                <span style={{ ...text.note, color: colour.inkFaint }}>and {plan.rows.length - 40} more</span>
              )}
            </Card>
          )}
        </>
      )}

      {confirming && plan && (
        <Dialog
          title={plan.needs_confirming ? 'Big job: confirm first' : 'Launch these runs'}
          says={
            plan.needs_confirming
              ? `This will start ${plan.runs} runs. That is more than ${plan.confirm_above}, so Maestro asks first.`
              : `This will start ${plan.runs} ${plan.runs === 1 ? 'run' : 'runs'}.`
          }
          confirm={`Launch ${plan.runs}`}
          onConfirm={() => void launch()}
          onClose={() => setConfirming(false)}
        >
          <Field label="Owner (who answers for the results)">
            <TextInput value={owner} onChange={setOwner} placeholder="Leave blank for each step's default owner" />
          </Field>
          <span style={{ ...text.note, color: colour.inkSoft }}>
            Launching by hand resets the automatic re-run count for every step it touches.
          </span>
          {plan.skipped.length > 0 && (
            <Status value="blocked" detail={`${plan.skipped.length} passes are skipped`} />
          )}
        </Dialog>
      )}

      {toast && <DoneToast says={toast} onClose={() => setToast(null)} />}
    </Page>
  );
}
